// The dock form (Incoming Inspection Report) as data: what the Receive screen
// offers, what it requires before it lets anyone submit, and what it sends.
//
// TWO STAGES BY TWO PEOPLE (Amit, 27 Oct 2026; migration 033, contract "the receiving
// handoff"): the warehouse records the VEHICLE inspection at the truck (RECEIVE) and hands
// over to QA, who records the GOODS inspection after it (RELEASE). Each is its own record,
// so a vehicle check's photo belongs to the vehicle record and a batch photo to the goods one.
//
// The server decides (api/src/domain/iirRules.ts, migration 017); this file
// applies the same rules first, so the person at the dock writes the reason
// instead of being refused for leaving it out. Pure, so it is tested without a
// browser.
import { iirRegisterWord, type RegisterRow, type Tone } from '../office/goodsInModel';

/** ⚠ Mirrors CK_DeliveryInspection_Stage of migration 033 (vocabulary.test.ts reads the next line as text). */
export const INSPECTION_STAGES = ['FULL', 'VEHICLE', 'GOODS'] as const;
export type InspectionStage = (typeof INSPECTION_STAGES)[number];

/** ⚠ Mirrors the PASS/FAIL CHECK constraints of migrations 017 and 022 (vocabulary.test.ts). */
export type CheckResult = 'PASS' | 'FAIL';

/** The seven truck checks, in the paper form's order (Anna's template, 23 Oct 2026). Keys are the API's. */
export const VEHICLE_CHECKS = [
  { key: 'COVERED_SEALED', label: 'Covered and securely closed, no evidence of tampering' },
  { key: 'CLEAN_EXTERIOR', label: 'Clean exterior' },
  { key: 'CLEAN_INTERIOR', label: 'Clean interior' },
  { key: 'NO_SPILLS_WASTE', label: 'No chemical spills or general waste' },
  { key: 'NO_OFF_ODOURS', label: 'Free of off odours' },
  { key: 'NO_PESTS', label: 'No evidence of pest infestation' },
  { key: 'NO_NON_FOOD_GOODS', label: 'Not shipped with non-food-grade or cross-contaminating goods' },
] as const;

/** The six product checks per batch row, in the paper form's order. The template's CofA column
 *  comes after AS_PER_SPEC; the app computes it in the office, so it is not asked here. */
export const BATCH_CHECKS = [
  { key: 'AS_PER_SPEC', label: 'Product as per specification' },
  { key: 'APPEARANCE', label: 'Appearance' },
  { key: 'COLOUR', label: 'Colour' },
  { key: 'ODOUR', label: 'Odour' },
  { key: 'FOREIGN_MATERIAL', label: 'Foreign material' },
  { key: 'INFESTATION', label: 'Infestation' },
] as const;

export type VehicleKey = (typeof VEHICLE_CHECKS)[number]['key'];
export type BatchKey = (typeof BATCH_CHECKS)[number]['key'];

/** ⚠ Mirrors CK_InspectionPhoto_CheckCode of migration 019, widened by 022 (vocabulary.test.ts reads it):
 *  what a photo may be of, besides the delivery or a row. */
export type PhotoCheckCode =
  | 'COVERED_SEALED'
  | 'CLEAN_EXTERIOR'
  | 'CLEAN_INTERIOR'
  | 'NO_SPILLS_WASTE'
  | 'NO_OFF_ODOURS'
  | 'NO_PESTS'
  | 'NO_NON_FOOD_GOODS'
  | 'AS_PER_SPEC'
  | 'APPEARANCE'
  | 'COLOUR'
  | 'ODOUR'
  | 'FOREIGN_MATERIAL'
  | 'INFESTATION';

/** The server's caps (api/src/domain/iirPhotos.ts): ten photos, 8 MB each after scaling. */
export const MAX_PHOTOS = 10;
export const MAX_PHOTO_BYTES = 8 * 1024 * 1024;

/**
 * A photo taken on the form, before it is submitted. ALWAYS OPTIONAL (Amit, 22 Oct 2026):
 * nothing in problems() ever asks for one. Already scaled to a JPEG (photoScale.ts).
 */
export interface DraftPhoto {
  key: string;
  blob: Blob;
  capturedAtDevice: string;
  /** The row it is of (DraftRow.key), or null for the delivery. */
  rowKey: string | null;
  /** The check it is of, or null. A vehicle check has no row; a product check has one. */
  checkCode: PhotoCheckCode | null;
}

/** "PO026141 / 2": the line number is always shown - two lines of one PO otherwise read alike. */
export function poLine(po: string, line: number): string {
  return `${po} / ${line}`;
}

/** Unanswered is `null`, never PASS: a check nobody looked at is not a pass. */
export interface Answer {
  result: CheckResult | null;
  reason: string;
}

export interface DraftRow {
  /** Local key for React; not sent. */
  key: string;
  receiptId: number;
  po: string;
  line: number;
  itemName: string;
  /** The D365 batch the row was prefilled from, when there was one. */
  batchId: string | null;
  lotAsPrinted: string;
  manufacturedDate: string;
  bestBeforeDate: string;
  /** Typed on the keypad; a string until sent. */
  quantityKg: string;
  checks: Record<BatchKey, Answer>;
  comment: string;
}

/** Stage 1, the warehouse at the truck: which PO lines are on it and the seven vehicle checks. No batches. */
export interface VehicleDraft {
  stage: 'VEHICLE';
  vendorAccount: string | null;
  vendorName: string;
  /** The PO lines ticked as on the truck (received in D365 or not). */
  lines: Array<{ receiptId: number; po: string; line: number; itemName: string }>;
  dateReceived: string;
  deliveryCompany: string;
  vehicleRego: string;
  vehicle: Record<VehicleKey, Answer>;
  comments: string;
  photos: DraftPhoto[];
}

/** Stage 2, QA after the vehicle passed: the batch rows. Carrier, rego, date and supplier are the vehicle record's. */
export interface GoodsDraft {
  stage: 'GOODS';
  /** The vehicle inspection it follows, or null when QA records the goods on their own (Amit, 2 Nov
   *  2026: the two steps open independently). */
  vehicleInspection: AwaitingVehicle | null;
  vendorAccount: string | null;
  vendorName: string;
  /** The PO lines being inspected: the vehicle record's, or the ones QA picked. */
  lines: Array<{ receiptId: number; po: string; line: number; item: string; itemName: string | null }>;
  /** Only on a goods inspection on its own: the truck as QA saw it (a vehicle record carries these otherwise). */
  truck: { dateReceived: string; deliveryCompany: string; vehicleRego: string } | null;
  rows: DraftRow[];
  /** PO lines the warehouse ticked that QA finds are not on the truck, each with QA's reason (audit
   *  2026-10-28): left out rather than attested to, and said in the record's comments. */
  leftOut: LeftOutLine[];
  comments: string;
  photos: DraftPhoto[];
}

export interface LeftOutLine {
  receiptId: number;
  po: string;
  line: number;
  itemName: string;
  reason: string;
}

export type Draft = VehicleDraft | GoodsDraft;

/**
 * One vehicle inspection QA still has to follow with a goods inspection, as
 * GET /receipt-inspections?awaiting=goods lists it (api/src/tools/iir.ts).
 */
export interface AwaitingVehicle {
  inspectionId: number;
  stage: InspectionStage;
  vendor: { account: string | null; name: string | null };
  deliveryCompany: string | null;
  vehicleRego: string | null;
  dateReceived: string | null;
  vehicle: Array<{ check: string; label: string; result: CheckResult | null; reason: string | null }>;
  inspectedByUpn: string;
  recordedAt: string | null;
  lines: Array<{ receiptId: number; po: string; line: number; item: string; itemName: string | null }>;
  goodsInspectionId: number | null;
  /** Null = the vehicle never failed. A failure closed with the load accepted still lets the goods follow. */
  failure?: { status: 'OPEN' | 'CLOSED'; closure?: { outcome?: string | null } | null } | null;
}

/**
 * `?vehicle=12` - the vehicle inspection a "Vehicle passed: inspect the goods" Teams card opens the
 * goods form on. A positive whole number, else null.
 */
export function linkedVehicleId(param: string | null): number | null {
  const s = (param ?? '').trim();
  return /^[1-9]\d{0,9}$/.test(s) ? Number(s) : null;
}

/** "Passed", or how a failed vehicle came to let the goods follow. */
export function vehicleOutcome(v: AwaitingVehicle): string {
  if (!v.failure) return 'Passed';
  const outcome = v.failure.closure?.outcome ?? null;
  if (v.failure.status === 'CLOSED' && outcome === 'ACCEPTED') return 'Failed; QA closed it, load accepted';
  if (v.failure.status === 'CLOSED' && outcome === 'ACCEPTED_WITH_CONDITIONS') return 'Failed; QA closed it, load accepted with conditions';
  return 'Failed';
}

export interface DeliveryGroup {
  vendorKey: string;
  vendorName: string;
  lines: RegisterRow[];
}

const DAY_MS = 86_400_000;

/** Today in Brisbane, YYYY-MM-DD: the same day the server's brisbaneDay gives, whatever the
 *  tablet's own time zone (review round 2). */
export function brisbaneToday(at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane', year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}
const addDays = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

/** Due this close to today, either side, is "today's deliveries" at the dock. */
const NEAR_DAYS = 3;

/** The register window "every open line" reads (the API allows at most 62 days): a month either side. */
export function openLinesQuery(today: string): string {
  return `/receipts?from=${addDays(today, -30)}&to=${addDays(today, 31)}`;
}

/**
 * Today's deliveries, from the register snapshot: ingredient lines expected
 * within a few days of today, or with a batch received in that time, grouped by
 * supplier. A cancelled line is not coming.
 *
 * `allOpen` (audit 2026-10-28): every line still to come in (D365 status Backorder), whatever its
 * date, as well - a truck days early or late is checked before D365 receives it, which is the
 * order the dock works in. The near lines stay, so a supplier's in-window lines are never lost.
 */
export function deliveryGroups(rows: readonly RegisterRow[], today: string, allOpen = false): DeliveryGroup[] {
  const from = addDays(today, -NEAR_DAYS);
  const to = addDays(today, NEAR_DAYS);
  const near = (d: string | null | undefined) => !!d && d >= from && d <= to;
  const groups = new Map<string, DeliveryGroup>();
  for (const r of rows) {
    if (r.lineStatus === 'Canceled') continue;
    const open = allOpen && r.lineStatus === 'Backorder';
    if (!open && !near(r.expectedDate) && !r.batches.some((b) => near(b.receivedDate))) continue;
    const vendorKey = r.vendor.account ?? r.vendor.name ?? 'unknown';
    const g = groups.get(vendorKey) ?? { vendorKey, vendorName: r.vendor.name ?? r.vendor.account ?? 'Unknown supplier', lines: [] };
    g.lines.push(r);
    groups.set(vendorKey, g);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, lines: g.lines.slice().sort((a, b) => a.po.localeCompare(b.po) || a.line - b.line) }))
    .sort((a, b) => a.vendorName.localeCompare(b.vendorName));
}

/**
 * `?receipt=7,8` - the PO lines a "received, inspect now" Teams card opens the form on (rule 26).
 * Positive whole numbers only; anything else in the list is ignored.
 */
export function linkedReceiptIds(param: string | null): number[] {
  if (!param) return [];
  return [...new Set(param.split(',').map((s) => s.trim()).filter((s) => /^[1-9]\d{0,9}$/.test(s)).map(Number))];
}

/**
 * The delivery a link names: the supplier group holding the first linked line found in the
 * register, with the linked lines of that supplier ticked. A line outside today's groups (received
 * more than a few days ago) still opens, in a group of its own supplier's linked lines. Null when
 * none of the lines is in the register at all - the screen says so rather than show nothing.
 */
export function linkedDelivery(rows: readonly RegisterRow[], today: string, ids: readonly number[]): { group: DeliveryGroup; picked: number[] } | null {
  const linked = ids.map((id) => rows.find((r) => r.receiptId === id)).filter((r): r is RegisterRow => !!r);
  if (linked.length === 0) return null;
  const first = linked[0];
  const vendorKey = first.vendor.account ?? first.vendor.name ?? 'unknown';
  const sameVendor = linked.filter((r) => (r.vendor.account ?? r.vendor.name ?? 'unknown') === vendorKey);
  const near = deliveryGroups(rows, today).find((g) => g.vendorKey === vendorKey);
  const lines = near ? [...near.lines, ...sameVendor.filter((r) => !near.lines.includes(r))] : sameVendor;
  const group: DeliveryGroup = { vendorKey, vendorName: first.vendor.name ?? first.vendor.account ?? 'Unknown supplier', lines };
  return { group, picked: sameVendor.map((r) => r.receiptId) };
}

const blank = <K extends string>(defs: ReadonlyArray<{ key: K }>) =>
  Object.fromEntries(defs.map((d) => [d.key, { result: null, reason: '' }])) as Record<K, Answer>;

let seq = 0;
const nextKey = () => `row-${++seq}`;

/** A blank product/batch row for a PO line. */
export function blankRow(line: RegisterRow): DraftRow {
  return {
    key: nextKey(),
    receiptId: line.receiptId,
    po: line.po,
    line: line.line,
    itemName: line.itemName ?? line.item,
    batchId: null,
    lotAsPrinted: '',
    manufacturedDate: '',
    bestBeforeDate: '',
    quantityKg: '',
    checks: blank(BATCH_CHECKS),
    comment: '',
  };
}

/**
 * The rows a PO line starts with: one per batch D365 received it into recently,
 * with the batch (shown beside the lot as a hint) and its best before; else one
 * blank row.
 *
 * ⚠ THE LOT IS LEFT EMPTY. It is what is printed on the bag, typed by the person
 * holding it. Prefilled from D365 it would be D365 compared with the certificate,
 * not the bag - rule 19's shape: a value tidied to agree is not evidence (review
 * round 1). An empty lot is NO_LOT in the office, never a match.
 */
export function rowsFor(line: RegisterRow, today: string): DraftRow[] {
  const since = addDays(today, -NEAR_DAYS);
  const recent = line.batches.filter((b) => !b.receivedDate || b.receivedDate >= since);
  if (recent.length === 0) return [blankRow(line)];
  return recent.map((b) => ({
    ...blankRow(line),
    batchId: b.batchId,
    lotAsPrinted: '',
    bestBeforeDate: b.bestBeforeDate ?? '',
  }));
}

/** A vehicle inspection of the lines ticked as on the truck. */
export function newVehicleDraft(group: DeliveryGroup, picked: readonly RegisterRow[], today: string): VehicleDraft {
  return {
    stage: 'VEHICLE',
    vendorAccount: group.lines[0]?.vendor.account ?? null,
    vendorName: group.vendorName,
    lines: picked.map((l) => ({ receiptId: l.receiptId, po: l.po, line: l.line, itemName: l.itemName ?? l.item })),
    dateReceived: today,
    deliveryCompany: '',
    vehicleRego: '',
    vehicle: blank(VEHICLE_CHECKS),
    comments: '',
    photos: [],
  };
}

/** The fields of a register row that a blank batch row reads, from a vehicle record's line. */
export function lineAsRegister(l: AwaitingVehicle['lines'][number]): RegisterRow {
  return { receiptId: l.receiptId, po: l.po, line: l.line, item: l.item, itemName: l.itemName } as RegisterRow;
}

/**
 * A goods inspection following a vehicle inspection: the rows each PO line on the truck starts
 * with. A line the goods-in register holds starts from what D365 received (rowsFor: the batch as
 * a hint, the lot left empty); a line it does not hold starts with one blank row.
 */
export function newGoodsDraft(v: AwaitingVehicle, register: readonly RegisterRow[], today: string): GoodsDraft {
  return {
    stage: 'GOODS',
    vehicleInspection: v,
    vendorAccount: v.vendor.account ?? null,
    vendorName: v.vendor.name ?? v.vendor.account ?? 'Unknown supplier',
    lines: v.lines.map((l) => ({ receiptId: l.receiptId, po: l.po, line: l.line, item: l.item, itemName: l.itemName })),
    truck: null,
    rows: v.lines.flatMap((l) => {
      const reg = register.find((r) => r.receiptId === l.receiptId);
      return reg ? rowsFor(reg, today) : [blankRow(lineAsRegister(l))];
    }),
    leftOut: [],
    comments: '',
    photos: [],
  };
}

/**
 * A goods inspection on its own (2 Nov 2026): the lines QA picked from the register, the truck's
 * date today, carrier and rego blank until QA types them. Nothing is copied from a vehicle record,
 * because there is none; the server gates it on nothing.
 */
export function newGoodsDraftApart(group: DeliveryGroup, picked: readonly RegisterRow[], today: string): GoodsDraft {
  return {
    stage: 'GOODS',
    vehicleInspection: null,
    vendorAccount: group.lines[0]?.vendor.account ?? null,
    vendorName: group.vendorName,
    lines: picked.map((l) => ({ receiptId: l.receiptId, po: l.po, line: l.line, item: l.item, itemName: l.itemName ?? null })),
    truck: { dateReceived: today, deliveryCompany: '', vehicleRego: '' },
    rows: picked.flatMap((l) => rowsFor(l, today)),
    leftOut: [],
    comments: '',
    photos: [],
  };
}

/**
 * QA leaves a PO line out of the goods inspection - the warehouse ticked it, but it is not on the
 * truck (audit 2026-10-28). Its rows and their photos go; the line waits for a reason.
 */
export function leaveOutLine(draft: GoodsDraft, receiptId: number): GoodsDraft {
  const l = draft.lines.find((x) => x.receiptId === receiptId);
  if (!l || draft.leftOut.some((x) => x.receiptId === receiptId)) return draft;
  const gone = new Set(draft.rows.filter((r) => r.receiptId === receiptId).map((r) => r.key));
  return {
    ...draft,
    rows: draft.rows.filter((r) => !gone.has(r.key)),
    photos: draft.photos.filter((p) => p.rowKey === null || !gone.has(p.rowKey)),
    leftOut: [...draft.leftOut, { receiptId, po: l.po, line: l.line, itemName: l.itemName ?? l.item, reason: '' }],
  };
}

/** A line left out by mistake, put back with one blank row. */
export function restoreLine(draft: GoodsDraft, receiptId: number): GoodsDraft {
  const l = draft.lines.find((x) => x.receiptId === receiptId);
  if (!l) return draft;
  return { ...draft, leftOut: draft.leftOut.filter((x) => x.receiptId !== receiptId), rows: [...draft.rows, blankRow(lineAsRegister(l))] };
}

/**
 * The draft's photos as the queue sends them, after the inspection: each with the row's
 * sequence on the inspection (its 1-based place in `rows`, as the server numbers them). A
 * photo of a row that was removed goes with the row. A vehicle draft has no rows, so its
 * photos are of the delivery or a vehicle check - the vehicle record's, never the goods'.
 */
export function photosToSend(draft: Draft): Array<{ blob: Blob; capturedAtDevice: string; batchSequence: number | null; checkCode: PhotoCheckCode | null }> {
  const seq = new Map((draft.stage === 'GOODS' ? draft.rows : []).map((r, i) => [r.key, i + 1]));
  return draft.photos
    .filter((p) => p.rowKey === null || seq.has(p.rowKey))
    .slice(0, MAX_PHOTOS)
    .map((p) => ({
      blob: p.blob,
      capturedAtDevice: p.capturedAtDevice,
      batchSequence: p.rowKey === null ? null : seq.get(p.rowKey)!,
      checkCode: p.checkCode,
    }));
}

/** The supplier a draft is of, as "Resume the inspection of <supplier>" names it (UI/UX audit, 31 Oct 2026, UX-08). */
export function draftSupplier(draft: Draft): string {
  return draft.vendorName;
}

const isAnswer = (a: unknown): boolean =>
  !!a && typeof a === 'object' && typeof (a as Answer).reason === 'string' &&
  ((a as Answer).result === null || (a as Answer).result === 'PASS' || (a as Answer).result === 'FAIL');
const allAnswered = (checks: unknown, keys: ReadonlyArray<{ key: string }>): boolean =>
  !!checks && typeof checks === 'object' && keys.every((c) => isAnswer((checks as Record<string, unknown>)[c.key]));

/**
 * A draft read back from the tablet, if this build can still render it; else null (UI/UX audit,
 * 31 Oct 2026, UX-08).
 *
 * WHY. A draft outlives the build that kept it: a tablet can sit on yesterday's half-done
 * inspection through a deploy that added a check (migration 022 added two). Handed to the form as
 * it is, a missing check would crash the screen, or - worse - render without the new check and
 * read as complete. So the shape is checked against THIS build's check lists, and a draft that does
 * not fit is not resumed: the screen says so and offers its owner Discard. Nothing is guessed in
 * (a check is never filled in for the person).
 */
export function resumableDraft(value: unknown): Draft | null {
  if (!value || typeof value !== 'object') return null;
  const d = value as Partial<VehicleDraft> & Partial<GoodsDraft>;
  if (!Array.isArray(d.photos) || typeof d.comments !== 'string') return null;
  if (d.stage === 'VEHICLE') {
    if (typeof d.vendorName !== 'string' || !Array.isArray(d.lines) || typeof d.dateReceived !== 'string') return null;
    if (typeof d.deliveryCompany !== 'string' || typeof d.vehicleRego !== 'string') return null;
    return allAnswered(d.vehicle, VEHICLE_CHECKS) ? (d as VehicleDraft) : null;
  }
  if (d.stage === 'GOODS') {
    const v = d.vehicleInspection;
    if (v !== null && (!v || typeof v !== 'object' || typeof v.inspectionId !== 'number' || !Array.isArray(v.lines) || !v.vendor)) return null;
    // A draft kept before 2 Nov 2026 has no lines, vendorName or truck of its own: it cannot be opened in this build.
    if (typeof d.vendorName !== 'string' || !Array.isArray(d.lines)) return null;
    if (v === null && !(d.truck && typeof d.truck === 'object' && typeof d.truck.dateReceived === 'string')) return null;
    if (!Array.isArray(d.rows) || !Array.isArray(d.leftOut)) return null;
    return d.rows.every((r) => r && typeof r.key === 'string' && typeof r.quantityKg === 'string' && allAnswered(r.checks, BATCH_CHECKS))
      ? (d as GoodsDraft)
      : null;
  }
  return null;
}

/** Same rule as the server's: a letter or a digit once invisible characters are gone. */
export function visible(text: string): boolean {
  return /[\p{L}\p{N}]/u.test(text.replace(/[\p{Cf}ᅟᅠㅤﾠ]/gu, ''));
}

/** The server's limit (iirRules MAX_QUANTITY_KG). */
export const MAX_QUANTITY_KG = 1e9;

/** A YYYY-MM-DD that reads back as itself (2026-02-30 is not a day). */
export function realDay(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/**
 * The DOM id of a check on the form, and of the fields a gap can point at (UI/UX audit, 31 Oct
 * 2026, UX-44): a gap names the place to jump to, so "is not answered" is never a sentence the
 * person has to go and find. A vehicle check has no row (null); a product check is its row's.
 */
export function checkId(rowKey: string | null, key: string): string {
  return `iir-check-${rowKey ?? 'vehicle'}-${key}`;
}
export function fieldId(what: string, owner: string | number | null = null): string {
  return `iir-field-${owner ?? 'delivery'}-${what}`;
}

/** One thing that stops a submit: the sentence, and the id of what to fix (null = nothing to jump to). */
export interface Gap {
  text: string;
  target: string | null;
}

/** What still stops a submit, in the form's order, each with where it is (UX-44). Empty = ready.
 *  `today` is the local day: nothing is received in the future. problems() is its sentences. */
export function gaps(draft: Draft, today?: string): Gap[] {
  const out: Gap[] = [];
  const check = (where: string, label: string, a: Answer, target: string) => {
    if (a.result === null) out.push({ text: `${where}: "${label}" is not answered.`, target });
    else if (a.result === 'FAIL' && !visible(a.reason)) out.push({ text: `${where}: "${label}" is FAIL - write the reason.`, target });
  };
  if (draft.stage === 'VEHICLE') {
    if (!realDay(draft.dateReceived)) out.push({ text: 'Date received is required.', target: fieldId('dateReceived') });
    else if (today && draft.dateReceived > today) out.push({ text: 'Date received is in the future.', target: fieldId('dateReceived') });
    if (draft.lines.length === 0) out.push({ text: 'Tick at least one PO line on the truck.', target: null });
    for (const c of VEHICLE_CHECKS) check('Vehicle', c.label, draft.vehicle[c.key], checkId(null, c.key));
    return out;
  }
  if (draft.truck) {
    if (!realDay(draft.truck.dateReceived)) out.push({ text: 'Date received is required.', target: fieldId('dateReceived') });
    else if (today && draft.truck.dateReceived > today) out.push({ text: 'Date received is in the future.', target: fieldId('dateReceived') });
  }
  if (draft.rows.length === 0) out.push({ text: 'Add at least one product/batch row.', target: null });
  for (const l of draft.leftOut ?? []) {
    if (!visible(l.reason)) {
      out.push({ text: `${poLine(l.po, l.line)} ${l.itemName} is left out: say why (for example, not on the truck).`, target: fieldId('leftOut', l.receiptId) });
    }
  }
  draft.rows.forEach((r, i) => {
    for (const c of BATCH_CHECKS) check(`Row ${i + 1} (${r.itemName})`, c.label, r.checks[c.key], checkId(r.key, c.key));
    const q = Number(r.quantityKg);
    if (r.quantityKg.trim() && !(Number.isFinite(q) && q > 0 && q <= MAX_QUANTITY_KG)) {
      out.push({ text: `Row ${i + 1}: quantity (kg) must be a number above 0.`, target: fieldId('quantityKg', r.key) });
    }
    if (r.manufacturedDate && !realDay(r.manufacturedDate)) out.push({ text: `Row ${i + 1}: date of manufacture is not a date.`, target: fieldId('manufacturedDate', r.key) });
    if (r.bestBeforeDate && !realDay(r.bestBeforeDate)) out.push({ text: `Row ${i + 1}: best before is not a date.`, target: fieldId('bestBeforeDate', r.key) });
    if (realDay(r.manufacturedDate) && realDay(r.bestBeforeDate) && r.manufacturedDate > r.bestBeforeDate) {
      out.push({ text: `Row ${i + 1}: date of manufacture is after best before.`, target: fieldId('manufacturedDate', r.key) });
    }
  });
  return out;
}

/** What still stops a submit, in the form's order. Empty = ready. The submit gate reads this. */
export function problems(draft: Draft, today?: string): string[] {
  return gaps(draft, today).map((g) => g.text);
}

/**
 * "5 of 7 checks answered" (UX-44): the neutral line a fresh form shows in place of the amber list.
 * A FAIL still waiting for its reason counts as answered here - the reason is a gap problems() still
 * names, and the submit gate still refuses it.
 */
export function answeredCount(draft: Draft): { answered: number; total: number } {
  const answers =
    draft.stage === 'VEHICLE'
      ? VEHICLE_CHECKS.map((c) => draft.vehicle[c.key])
      : draft.rows.flatMap((r) => BATCH_CHECKS.map((c) => r.checks[c.key]));
  return { answered: answers.filter((a) => a.result !== null).length, total: answers.length };
}

/**
 * A PO line's dock state in the "which lines are on this truck?" picker (UI/UX audit, 31 Oct 2026,
 * UX-43): the office register's own word (iirRegisterWord - never a second wording), so a line that
 * already has a vehicle check reads as that. `inspected` null = the register did not say (an API
 * older than 017): "not known", never "not inspected" (rule 15).
 */
export function lineInspection(r: Pick<RegisterRow, 'iir'>): { text: string; tone: Tone; inspected: boolean | null } {
  if (!r.iir) return { text: 'Inspection not known', tone: 'warn', inspected: null };
  return { ...iirRegisterWord(r.iir), inspected: r.iir.status !== 'NONE' };
}

/**
 * The picker's order (UX-43): lines with no inspection first, then the ones not known, then the ones
 * already checked - stable, so PO and line order hold within each. Nothing is taken off the list: a
 * second truck on a line already checked is legitimate, and the server decides.
 */
export function pickerLines<R extends Pick<RegisterRow, 'iir'>>(lines: readonly R[]): R[] {
  const rank = (r: R) => {
    const i = lineInspection(r).inspected;
    return i === false ? 0 : i === null ? 1 : 2;
  };
  return lines.map((r, i) => ({ r, i })).sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i).map((x) => x.r);
}

/**
 * ONE date format on the dock (UI/UX audit, 31 Oct 2026, UX-53). The screen used three - ISO
 * ("2026-10-28"), the register's dayLabel ("28 Oct", year dropped) and the tablet's own
 * toLocaleString (whatever locale and zone the iPad is set to). Now: en-AU, "28 Oct 2026", and a
 * moment adds the Brisbane time, "28 Oct 2026, 2:15 pm" - Brisbane, as brisbaneToday is, so the day
 * shown is the plant's whatever the tablet's zone. Anything unreadable is shown as it came.
 */
export function dockDay(day: string | null | undefined): string {
  if (!day) return 'Not known';
  const d = day.slice(0, 10);
  if (!realDay(d)) return day;
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));
}
export function dockTime(at: string | null | undefined): string {
  if (!at) return 'Not known';
  const t = new Date(at);
  if (Number.isNaN(t.getTime())) return at;
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Australia/Brisbane',
  }).format(t);
}

/** "today", "1 day ago", "3 days ago": how long a delivery has waited for the dock (UX-53). Null when not a day. */
export function waitedFor(day: string, today: string): string | null {
  const d = day.slice(0, 10);
  if (!realDay(d) || !realDay(today)) return null;
  const n = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${d}T00:00:00Z`)) / DAY_MS);
  if (n <= 0) return 'today';
  return n === 1 ? '1 day ago' : `${n} days ago`;
}

/** "Waiting for the dock", oldest first (UX-53): the delivery that has waited longest is the one to chase. Stable. */
export function oldestFirst<D extends { received: string }>(deliveries: readonly D[]): D[] {
  return deliveries
    .map((d, i) => ({ d, i }))
    .sort((a, b) => a.d.received.localeCompare(b.d.received) || a.i - b.i)
    .map((x) => x.d);
}

export function failedCount(draft: Draft): number {
  if (draft.stage === 'VEHICLE') return VEHICLE_CHECKS.filter((c) => draft.vehicle[c.key].result === 'FAIL').length;
  return draft.rows.reduce((n, r) => n + BATCH_CHECKS.filter((c) => r.checks[c.key].result === 'FAIL').length, 0);
}

const answer = (a: Answer) => ({ result: a.result, ...(a.result === 'FAIL' || a.reason.trim() ? { reason: a.reason.trim() } : {}) });
const orNull = (s: string) => (s.trim() ? s.trim() : null);

/**
 * The body POST /receipt-inspections takes, minus what the queue stamps
 * (clientInspectionId and capturedAtDevice). No UPN: the server takes it from the
 * token (rule 2).
 *
 * VEHICLE: the PO lines, carrier, rego, date and the seven checks - NO batches.
 * GOODS: the vehicle inspection it follows and the batch rows - NO vehicle, carrier, rego or
 * date: the server copies those from the vehicle record.
 */
export function toPayload(draft: Draft): Record<string, unknown> {
  if (draft.stage === 'VEHICLE') {
    return {
      stage: 'VEHICLE',
      receiptIds: draft.lines.map((l) => l.receiptId),
      dateReceived: draft.dateReceived,
      deliveryCompany: orNull(draft.deliveryCompany),
      vehicleRego: orNull(draft.vehicleRego),
      vehicle: Object.fromEntries(VEHICLE_CHECKS.map((c) => [c.key, answer(draft.vehicle[c.key])])),
      comments: orNull(draft.comments),
    };
  }
  return {
    stage: 'GOODS',
    // After a vehicle record: its id, and the server copies the truck from it. On its own: no id, and
    // the truck as QA saw it (2 Nov 2026).
    ...(draft.vehicleInspection
      ? { vehicleInspectionId: draft.vehicleInspection.inspectionId }
      : { dateReceived: draft.truck?.dateReceived ?? '', deliveryCompany: orNull(draft.truck?.deliveryCompany ?? ''), vehicleRego: orNull(draft.truck?.vehicleRego ?? '') }),
    batches: draft.rows.map((r) => ({
      receiptId: r.receiptId,
      batchId: r.batchId,
      lotAsPrinted: orNull(r.lotAsPrinted),
      manufacturedDate: orNull(r.manufacturedDate),
      bestBeforeDate: orNull(r.bestBeforeDate),
      quantityKg: r.quantityKg.trim() ? Number(r.quantityKg) : null,
      checks: Object.fromEntries(BATCH_CHECKS.map((c) => [c.key, answer(r.checks[c.key])])),
      comment: orNull(r.comment),
    })),
    // A line left out is said in the record, in QA's words, before anything else they wrote.
    comments: orNull(
      [...(draft.leftOut ?? []).map((l) => `Not inspected: ${poLine(l.po, l.line)} ${l.itemName} - ${l.reason.trim()}`), draft.comments.trim()].filter(Boolean).join('\n'),
    ),
  };
}

/**
 * What to do about a refusal the tablet keeps, in the dock's words, beside the server's own
 * sentence. Null when the server's sentence says it all.
 */
export function refusalHint(reason: string | null): string | null {
  switch (reason) {
    case 'stage_required':
      return 'This was saved on the old one-page form. Discard it and record it again: the vehicle first, then the goods.';
    case 'vehicle_not_found':
    case 'not_a_vehicle_inspection':
      return 'Discard it, then pick the truck again from Goods to inspect.';
    case 'vehicle_superseded':
      return 'The vehicle inspection was corrected. Discard this and inspect the goods against the newer one.';
    case 'vehicle_failed':
      return 'The goods stay on the truck until QA closes the vehicle failure with the load accepted.';
    case 'line_not_on_vehicle':
      return 'Only the PO lines on that truck can be inspected. Discard this and record it again.';
    case 'goods_already_inspected':
      return 'Someone already inspected these goods. Discard this; QA corrects that record in the office if needed.';
    case 'left_out_unexplained':
      return 'A PO line on the truck has no row. Discard this and record it again, saying why that line is left out.';
    default:
      return null;
  }
}

/**
 * Whether the operator shell shows the Receiving link: to a RECEIVE holder, and to ANYONE while
 * this tablet holds inspections still to send or refused - those must never wait where nobody
 * can see them (review round 2). An unreadable queue (null) shows it too: unknown is not empty.
 */
export function showReceiving(holdsReceive: boolean, pending: number | null, refused: number): boolean {
  return holdsReceive || pending === null || pending > 0 || refused > 0;
}
